import { z } from "zod";
import { BusinessDate, Instant, Name, Note, Version } from "./common.js";
import { Id } from "./ids.js";

/**
 * Quantities are strictly integers representing thousandths of the stock unit
 * (e.g. 1.250 kg = 1250 milli-units, 2 pcs = 2000 milli-units).
 */
export const QuantityMilli = z.number().int();
export type QuantityMilli = z.infer<typeof QuantityMilli>;

export const PackConversion = z.object({
  packUnit: z.string().min(1),
  factorMilli: z.number().int().positive(),
});
export type PackConversion = z.infer<typeof PackConversion>;

export const Warehouse = z.object({
  id: Id,
  kitchenId: Id.nullable().optional(),
  code: z.string().min(1),
  name: Name,
  isVirtual: z.boolean().default(false),
  status: z.enum(["active", "archived"]).default("active"),
  version: Version.default(0).optional(),
  createdAt: Instant.optional(),
  updatedAt: Instant.optional(),
  archivedAt: Instant.nullable().optional(),
});
export type Warehouse = z.infer<typeof Warehouse>;

export const WarehouseInput = z.object({
  kitchenId: Id.nullable().optional(),
  code: z.string().min(1),
  name: Name,
  isVirtual: z.boolean().default(false),
  status: z.enum(["active", "archived"]).default("active"),
});
export type WarehouseInput = z.infer<typeof WarehouseInput>;

export const StockItem = z.object({
  id: Id,
  code: z.string().min(1),
  name: Name,
  category: z.string().min(1).default("raw"),
  baseUnit: z.string().min(1),
  minStockMilli: z.number().int().nonnegative().optional(),
  componentId: Id.nullable().optional(),
  conversions: z.array(PackConversion).default([]),
  status: z.enum(["active", "archived"]).default("active"),
  version: Version.default(0).optional(),
  createdAt: Instant.optional(),
  updatedAt: Instant.optional(),
  archivedAt: Instant.nullable().optional(),
});
export type StockItem = z.infer<typeof StockItem>;

export const StockItemInput = z.object({
  code: z.string().min(1),
  name: Name,
  category: z.string().min(1).default("raw"),
  baseUnit: z.string().min(1),
  minStockMilli: z.number().int().nonnegative().optional(),
  componentId: Id.nullable().optional(),
  conversions: z.array(PackConversion).default([]),
  status: z.enum(["active", "archived"]).default("active"),
});
export type StockItemInput = z.infer<typeof StockItemInput>;

export const VoucherKind = z.enum([
  "receipt",
  "issue",
  "return_to_supplier",
  "waste",
  "count",
  "transfer_send",
  "transfer_receive",
  "staff_meal",
  "staff-meal",
]);
export type VoucherKind = z.infer<typeof VoucherKind>;

export const VoucherLine = z.object({
  itemId: Id,
  quantityMilli: z.number().int().positive(),
  unitPriceRial: z.number().int().nonnegative().optional(),
  note: Note.optional(),
});
export type VoucherLine = z.infer<typeof VoucherLine>;

export const TransferShortageLine = z.object({
  itemId: Id,
  shortageMilli: z.number().int().positive(),
  reason: Note.optional(),
});
export type TransferShortageLine = z.infer<typeof TransferShortageLine>;

export const Voucher = z.object({
  id: Id,
  voucherNumber: z.string().min(1).nullable().optional(),
  year: z.number().int().positive().nullable().optional(),
  number: z.number().int().positive().nullable().optional(),
  date: BusinessDate,
  timestamp: Instant.optional(),
  warehouseId: Id,
  kind: VoucherKind,
  status: z.enum(["draft", "posted"]).default("posted").optional(),
  lines: z.array(VoucherLine).min(1),
  targetWarehouseId: Id.optional(),
  transferId: Id.optional(),
  shortageLines: z.array(TransferShortageLine).optional(),
  reason: Note.optional(),
  reversalOfVoucherId: Id.optional(),
  version: Version.default(0).optional(),
  createdAt: Instant.optional(),
  updatedAt: Instant.optional(),
  archivedAt: Instant.nullable().optional(),
});
export type Voucher = z.infer<typeof Voucher>;

export const VoucherInput = z.object({
  date: BusinessDate,
  warehouseId: Id,
  kind: VoucherKind,
  status: z.enum(["draft", "posted"]).default("posted").optional(),
  lines: z.array(VoucherLine).min(1),
  targetWarehouseId: Id.optional(),
  transferId: Id.optional(),
  shortageLines: z.array(TransferShortageLine).optional(),
  reason: Note.optional(),
  reversalOfVoucherId: Id.optional(),
  allowNegative: z.boolean().default(false).optional(),
});
export type VoucherInput = z.infer<typeof VoucherInput>;

export const VoucherUpdateInput = z.object({
  date: BusinessDate.optional(),
  warehouseId: Id.optional(),
  kind: VoucherKind.optional(),
  lines: z.array(VoucherLine).min(1).optional(),
  targetWarehouseId: Id.optional(),
  transferId: Id.optional(),
  shortageLines: z.array(TransferShortageLine).optional(),
  reason: Note.optional(),
  allowNegative: z.boolean().optional(),
});
export type VoucherUpdateInput = z.infer<typeof VoucherUpdateInput>;

export const PostVoucherInput = z.object({
  allowNegative: z.boolean().default(false).optional(),
});
export type PostVoucherInput = z.infer<typeof PostVoucherInput>;

export const BomLine = z.object({
  itemId: Id,
  quantityPerPortionMilli: z.number().int().positive(),
  wasteFactorMilli: z.number().int().positive().default(1000).optional(),
});
export type BomLine = z.infer<typeof BomLine>;

export const BomVersion = z.object({
  id: Id,
  componentId: Id.nullable().optional(),
  menuItemId: Id.nullable().optional(),
  tierId: Id.nullable().optional(),
  version: z.number().int().positive(),
  validFrom: BusinessDate,
  lines: z.array(BomLine).min(1),
  createdAt: Instant.optional(),
  updatedAt: Instant.optional(),
  archivedAt: Instant.nullable().optional(),
});
export type BomVersion = z.infer<typeof BomVersion>;

export const BomVersionInput = z.object({
  componentId: Id.optional(),
  menuItemId: Id.optional(),
  tierId: Id.optional(),
  validFrom: BusinessDate,
  lines: z.array(BomLine).min(1),
});
export type BomVersionInput = z.infer<typeof BomVersionInput>;

export const WarningCode = z.enum([
  "NEGATIVE_STOCK",
  "NO_COST_VALUATION",
  "COUNT_DISCREPANCY",
  "TRANSFER_SHORTAGE",
]);
export type WarningCode = z.infer<typeof WarningCode>;

export const InventoryWarning = z.object({
  code: WarningCode,
  itemId: Id,
  warehouseId: Id,
  voucherId: Id.optional(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});
export type InventoryWarning = z.infer<typeof InventoryWarning>;

export const StockBalance = z.object({
  warehouseId: Id,
  itemId: Id,
  quantityMilli: z.number().int(),
  totalValueRial: z.number().int(),
  averageUnitCostRial: z.number().int(),
});
export type StockBalance = z.infer<typeof StockBalance>;

export const KardexRow = z.object({
  voucherId: Id,
  voucherNumber: z.string(),
  date: BusinessDate,
  kind: VoucherKind,
  inQuantityMilli: z.number().int(),
  inUnitPriceRial: z.number().int(),
  inTotalRial: z.number().int(),
  outQuantityMilli: z.number().int(),
  outUnitPriceRial: z.number().int(),
  outTotalRial: z.number().int(),
  balanceQuantityMilli: z.number().int(),
  balanceAverageCostRial: z.number().int(),
  balanceTotalValueRial: z.number().int(),
});
export type KardexRow = z.infer<typeof KardexRow>;

export const ShortageForecastItem = z.object({
  warehouseId: Id,
  itemId: Id,
  neededMilli: z.number().int(),
  currentBalanceMilli: z.number().int(),
  shortageMilli: z.number().int(),
  estimatedCostRial: z.number().int().optional(),
});
export type ShortageForecastItem = z.infer<typeof ShortageForecastItem>;

export const ShortageForecast = z.object({
  date: BusinessDate,
  kitchenId: Id,
  items: z.array(ShortageForecastItem),
  totalEstimatedCostRial: z.number().int(),
});
export type ShortageForecast = z.infer<typeof ShortageForecast>;

export const StaffMeal = z.object({
  id: Id,
  businessDate: BusinessDate,
  kitchenId: Id,
  mealId: Id,
  menuItemId: Id,
  portions: z.number().int().positive(),
  voucherId: Id,
  note: Note.optional(),
  version: Version.default(0).optional(),
  createdAt: Instant.optional(),
  updatedAt: Instant.optional(),
  archivedAt: Instant.nullable().optional(),
});
export type StaffMeal = z.infer<typeof StaffMeal>;

export const StaffMealInput = z.object({
  businessDate: BusinessDate,
  kitchenId: Id,
  mealId: Id,
  menuItemId: Id,
  portions: z.number().int().positive(),
  note: Note.optional(),
});
export type StaffMealInput = z.infer<typeof StaffMealInput>;

export const CostFixedMode = z.enum(["per_portion", "monthly_share"]);
export type CostFixedMode = z.infer<typeof CostFixedMode>;

export const CostSettings = z.object({
  fixedMode: CostFixedMode.default("per_portion"),
  packagingCostRial: z.number().int().nonnegative().default(15000),
  breadCostRial: z.number().int().nonnegative().default(10000),
  staffSharePerPortionRial: z.number().int().nonnegative().default(80000),
  overheadSharePerPortionRial: z.number().int().nonnegative().default(40000),
  monthlyStaffPoolRial: z.number().int().nonnegative().default(2000000000),
  monthlyOverheadPoolRial: z.number().int().nonnegative().default(1000000000),
  monthlyPortionsEstimate: z.number().int().positive().default(25000),
  reconciliationThresholdPerMille: z.number().int().nonnegative().default(50),
});
export type CostSettings = z.infer<typeof CostSettings>;

export const CostSettingsInput = CostSettings.partial();
export type CostSettingsInput = z.infer<typeof CostSettingsInput>;

export const CostSnapshot = z.object({
  id: Id,
  businessDate: BusinessDate,
  menuItemId: Id,
  tierId: Id.nullable().optional(),
  materialsCostRial: z.number().int().nonnegative(),
  packagingCostRial: z.number().int().nonnegative(),
  breadCostRial: z.number().int().nonnegative(),
  fixedShareRial: z.number().int().nonnegative(),
  totalCostRial: z.number().int().nonnegative(),
  fixedMode: CostFixedMode,
  details: z.record(z.string(), z.unknown()).default({}),
  frozenAt: Instant.optional(),
  version: Version.default(0).optional(),
  createdAt: Instant.optional(),
  updatedAt: Instant.optional(),
  archivedAt: Instant.nullable().optional(),
});
export type CostSnapshot = z.infer<typeof CostSnapshot>;

export const ProductionReconciliationItem = z.object({
  businessDate: BusinessDate,
  kitchenId: Id,
  menuItemId: Id,
  foodName: z.string(),
  plannedPortions: z.number().int(),
  producedPortions: z.number().int(),
  deliveredPortions: z.number().int(),
  staffMealPortions: z.number().int(),
  differencePortions: z.number().int(),
  thresholdPerMille: z.number().int(),
  exceedsThreshold: z.boolean(),
});
export type ProductionReconciliationItem = z.infer<typeof ProductionReconciliationItem>;

export const ProductionReconciliationReport = z.object({
  businessDate: BusinessDate,
  kitchenId: Id,
  items: z.array(ProductionReconciliationItem),
});
export type ProductionReconciliationReport = z.infer<typeof ProductionReconciliationReport>;
